import { useState, useCallback, useEffect } from 'react';

// Track whether the last interaction was pointer-based.
// Set on mousedown/pointerdown, cleared on keydown.
// Module-level so all hook instances share the same signal.
let _pointerActive = false;

function _onPointerDown() { _pointerActive = true; }
function _onKeyDown()     { _pointerActive = false; }

let _listening = false;
function ensureListeners() {
  if (_listening) return;
  _listening = true;
  document.addEventListener('mousedown',    _onPointerDown, true);
  document.addEventListener('pointerdown',  _onPointerDown, true);
  document.addEventListener('keydown',      _onKeyDown,     true);
}

/**
 * Shows a focus ring only for keyboard navigation, not mouse/touch clicks.
 * Pass `{ includePointer: true }` for TEXT INPUTS: a field you tapped must
 * still show it is the active one (the keyboard-only rule is for buttons).
 */
export function useFocusRing(options = {}) {
  const { onFocus: onFocusProp, onBlur: onBlurProp, includePointer = false } = options;
  const [focused, setFocused] = useState(false);

  useEffect(() => { ensureListeners(); }, []);

  const onFocus = useCallback(
    (e) => {
      if (includePointer || !_pointerActive) {
        setFocused(true);
      }
      onFocusProp?.(e);
    },
    [onFocusProp, includePointer],
  );

  const onBlur = useCallback(
    (e) => {
      setFocused(false);
      onBlurProp?.(e);
    },
    [onBlurProp],
  );

  return { focused, focusProps: { onFocus, onBlur } };
}
